import { Payer } from "@redotech/congo-enums/payer";
import { Step as ModelStep } from "@redotech/redo-return-core-common/return-flow";
import { submitWarrantyFromModel } from "@redotech/redo-return-core-common/return-flow-step-from-model";
import {
  isSubmitWarrantyStepValid,
  SubmitWarrantyState,
} from "@redotech/redo-return-core-common/return-flow-step-valid";
import {
  manualLabelFields,
  manualLabelMode,
  ReturnOptionMethod,
} from "@redotech/redo-return-core-common/return-flow/return-option";
import {
  ManualLabelMode,
  StoreCreditType,
} from "@redotech/redo-return-core-common/return-flow/return-option-enums";
import { STEP_TYPE_NAMES } from "@redotech/redo-return-core-common/return-flow/step-type-names";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import RefreshIcon from "@redotech/redo-web/icon-old/refresh.svg";
import * as React from "react";
import { StepType, StepTypeDetailsProps } from "../step";
import { getModelReturnOptionMethod, signCurrencyOverride } from "./return";
import { returnOptionLabels } from "./submit-claim";

export const SUBMIT_WARRANTY = (
  Details: React.ComponentType<StepTypeDetailsProps<SubmitWarrantyState>>,
): StepType<SubmitWarrantyState, ModelStep.SubmitWarranty> => ({
  Details: (props: StepTypeDetailsProps<SubmitWarrantyState>) =>
    React.createElement(Details, props),
  customTitle(state) {
    return state.customTitle;
  },
  title: STEP_TYPE_NAMES[ModelStep.SUBMIT_WARRANTY],
  valid(state) {
    return isSubmitWarrantyStepValid(state).valid;
  },
  Icon: RefreshIcon,
  iconColor: "success",
  description(state) {
    return returnOptionLabels(state.option.method) || "";
  },
  downstream() {
    return [];
  },
  maxDownstream() {
    return 0;
  },
  empty: {
    option: {
      method: ReturnOptionMethod.EXCHANGE,
      creditType: StoreCreditType.GIFT_CARD,
      requireSendBack: true,
      labelPayer: Payer.Merchant,
      adjustmentFlat: "0",
      adjustmentPercent: "0",
      ...manualLabelFields(ManualLabelMode.OFF),
    },
  },
  layout() {
    return BlockLayout.FULL;
  },
  stepDeleted(state) {
    return state;
  },
  toModel(state) {
    const flat = parseFloat(state.option.adjustmentFlat ?? "0");
    return {
      customTitle: state.customTitle,
      type: ModelStep.SUBMIT_WARRANTY,
      option: {
        method: getModelReturnOptionMethod(
          state.option.method,
          state.option.creditType,
        ),
        adjustment: {
          flat,
          proportion:
            parseFloat(state.option.adjustmentPercent ?? "0") / 100 + 1,
          currencyOverrides: state.option.currencyOverrides
            ? Object.fromEntries(
                Object.entries(state.option.currencyOverrides)
                  .filter(([, v]) => v !== undefined && !isNaN(+v))
                  .map(([k, v]) => [k, signCurrencyOverride(+v, flat)]),
              )
            : undefined,
          perCurrencyAmountsEnabled: state.option.perCurrencyAmountsEnabled,
        },
        requireSendBack: state.option.requireSendBack,
        labelPayer: state.option.labelPayer,
        ...manualLabelFields(manualLabelMode(state.option)),
        discountTitle:
          state.option.method === ReturnOptionMethod.EXCHANGE
            ? state.option.discountTitle || undefined
            : undefined,
        // Only meaningful for same-item exchange — drop the value when
        // the merchant switches the method so a stale toggle doesn't
        // silently apply if they switch back to refund/store-credit.
        chargeCustomerForNewOrderShipping:
          state.option.method === ReturnOptionMethod.EXCHANGE
            ? state.option.chargeCustomerForNewOrderShipping
            : undefined,
        carryOverLineItemProperties:
          state.option.method === ReturnOptionMethod.EXCHANGE
            ? (state.option.carryOverLineItemProperties ?? true)
            : undefined,
        allowUnpublishedReplacement:
          state.option.method === ReturnOptionMethod.EXCHANGE
            ? state.option.allowUnpublishedReplacement
            : undefined,
      },
    };
  },
  fromModel(model) {
    return submitWarrantyFromModel(model);
  },
});

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export {
  type SubmitWarrantyOption,
  type SubmitWarrantyState,
} from "@redotech/redo-return-core-common/return-flow-step-valid";
